import {
  assistLicensesAtom,
  auditLicensesAtom,
  availableAuditEntriesAtom,
  translateEasyLicenses,
  userAtom,
} from '@/global-store';
import { isExpired } from '@/helpers/isExpired';
import {
  IconAccessible,
  IconChartLine,
  IconClipboardPlus,
  IconFileInfo,
  IconFileTypePdf,
  IconFlask,
  IconKey,
  IconLanguage,
  IconRadar,
  IconReport,
  type Icon,
} from '@tabler/icons-react';
import type { BaseLicense, TranslateData } from '@eye-able/types';
import { useAtomValue } from 'jotai';
import { AllyIcon, PlainLanguageIcon } from '@/components/customIcons';

export type Application = {
  icon: Icon | typeof AllyIcon | typeof PlainLanguageIcon;
  id: string;
  label: string;
  description: string;
  link: string;
  isNew?: boolean;
  hasLicense: boolean;
};

function isActiveLicense(license: BaseLicense) {
  if (!license?.endDate) return false;

  return !isExpired(license.endDate);
}

function hasActiveLicense(licenses?: BaseLicense[] | null) {
  return Boolean(licenses?.some((license) => isActiveLicense(license)));
}

function hasPositiveLimit(value?: number | null, limit: number = 0) {
  return Number(value) > limit;
}

function hasTranslateLimit(license: TranslateData) {
  if (!hasPositiveLimit(license.charLimit)) return false;
  if (!license.endDate) return true;

  return !isExpired(license.endDate);
}

function hasEasyLanguageLimit(license: TranslateData) {
  if (!hasPositiveLimit(license.charLimitEasy)) return false;
  if (!license.endDateEasy) return true;

  return !isExpired(license.endDateEasy);
}

export function useHomeApplications() {
  const user = useAtomValue(userAtom);

  const assistLicenses = useAtomValue(assistLicensesAtom);
  const auditLicenses = useAtomValue(auditLicensesAtom);
  const reportLicenses = useAtomValue(availableAuditEntriesAtom);
  const languageLicenses = useAtomValue(translateEasyLicenses);

  const activeReportLicenses =
    reportLicenses?.filter((license) => isActiveLicense(license)) ?? [];
  const hasReportLicense = activeReportLicenses.length > 0;

  const applications: Application[] = [
    {
      id: 'assist',
      label: 'Assist',
      description: 'assistShortDescription',
      icon: IconAccessible,
      link: '/assist/overview',
      hasLicense: hasActiveLicense(assistLicenses),
    },
    {
      id: 'easyLanguage',
      label: 'plainLanguage',
      description: 'plainLanguageShortDescription',
      icon: PlainLanguageIcon,
      link: '/plain-language/simplify',
      hasLicense: languageLicenses.some((license) =>
        hasEasyLanguageLimit(license),
      ),
    },
    {
      id: 'translate',
      label: 'Translate',
      description: 'translateShortDescription',
      icon: IconLanguage,
      link: '/translate/websites',
      hasLicense: languageLicenses.some((license) =>
        hasTranslateLimit(license),
      ),
    },
    {
      id: 'audit',
      label: 'Audit',
      description: 'auditShortDescription',
      icon: IconRadar,
      link: '/audit',
      hasLicense: hasActiveLicense(auditLicenses),
    },
    {
      id: 'report',
      label: 'Report',
      description: 'reportShortDescription',
      icon: IconReport,
      link: '/report/overview',
      hasLicense: hasReportLicense,
    },
    {
      id: 'access',
      label: 'Access',
      description: 'accessShortDescription',
      icon: IconKey,
      link: '/report/access/overview',
      hasLicense:
        hasReportLicense &&
        activeReportLicenses.some((license) =>
          hasPositiveLimit(license.suggestionsLimit, 3),
        ),
    },
    {
      id: 'guidedTesting',
      label: 'guidedTesting',
      description: 'guidedTestingShortDescription',
      icon: IconClipboardPlus,
      link: '/report/tests',
      hasLicense: hasReportLicense,
    },
    {
      id: 'pdfs',
      label: 'PDFs',
      description: 'pdfsShortDescription',
      icon: IconFileTypePdf,
      link: '/report/pdfs/',
      hasLicense: hasReportLicense,
    },
    {
      id: 'analysis',
      label: 'Content & Web Analysis',
      description: 'analysisShortDescription',
      icon: IconChartLine,
      link: '/report/analysis/broken-links',
      hasLicense: hasReportLicense,
    },
    {
      id: 'accessibilityStatement',
      label: 'accessibilityStatement',
      description: 'accessibilityStatementShortDescription',
      icon: IconFileInfo,
      link: '/report/accessibility-statement/generator',
      hasLicense:
        hasReportLicense &&
        activeReportLicenses.some((license) =>
          Boolean(license.statementGenerator),
        ),
    },
    {
      id: 'manualTests',
      label: 'manualTests',
      description: 'manualTestsShortDescription',
      icon: IconFlask,
      link: '/report/manual-tests',
      isNew: true,
      hasLicense: hasReportLicense,
    },
    {
      id: 'ally',
      label: 'allyAssistant',
      description: 'allyAssistantShortDescription',
      icon: AllyIcon,
      link: '/ally',
      hasLicense: hasPositiveLimit(user?.allyCharLimit),
    },
  ];

  return applications;
}
